'use client'

import React, {useState, useRef, useEffect} from 'react'
import ReactCalendar from 'react-calendar'
import {DateRange, formatDateWithLocale, getDateRange} from '@local/utils/date'
import ChevronSVG from '@local/assets/svgs/chevron.svg'
import DateSVG from '@local/assets/svgs/icon-date.svg'
import {useEscapeKey, useFocusTrap} from '@local/hooks'
import {Button} from '@local/ui/components'
import CloseSVG from '@local/assets/svgs/icon-close.svg'
const DEFAULT_LOCALE = 'en'

const QUICK_DATE_OPTIONS = [
	{label: 'Next Week', name: DateRange.WEEK},
	{label: 'Next Month', name: DateRange.MONTH},
	{label: 'Next 3 Months', name: DateRange.THREE_MONTHS},
]

type ValuePiece = Date | null

type Value = ValuePiece | [ValuePiece, ValuePiece]

interface DatePickerComponentProps {
	value: Value
	onChange: (value: Value) => void
	minDate?: Date
	maxDate?: Date
	allowRange?: boolean
	locale?: string
	showPresets?: boolean
}

export const Calendar: React.FC<DatePickerComponentProps> = ({
	value,
	onChange,
	minDate,
	maxDate,
	allowRange = true,
	locale = DEFAULT_LOCALE,
	showPresets = true,
}) => {
	const [isModalOpen, setModalOpen] = useState(false)
	const [showModal, setShowModal] = useState(false)
	const buttonRef = useRef<HTMLDivElement>(null)
	const modalRef = useRef<HTMLDivElement>(null)

	const openModal = () => {
		setShowModal(true)
		setTimeout(() => {
			setModalOpen(true)
		}, 10)
	}

	const closeModal = () => {
		setModalOpen(false)
		setTimeout(() => {
			setShowModal(false)
		}, 300)
	}

	const trapRef = useFocusTrap({
		includeContainer: true,
		initialFocus: 'container',
		returnFocus: true,
		updateNodes: true,
		isActive: showModal,
		onClickClose: closeModal,
	})

	const handleQuickSelection = (type: DateRange) => onChange(getDateRange(type))
	const handleClear = () => onChange(null)
	const handleApply = () => closeModal()

	const formatValue = () => {
		if (!value) return 'Date'
		if (Array.isArray(value)) {
			if (!value[0] || !value[1]) return 'Date'
			return `${formatDateWithLocale(value[0], locale)} - ${formatDateWithLocale(value[1], locale)}`
		}
		return formatDateWithLocale(value, locale)
	}

	useEscapeKey(() => {
		if (showModal) closeModal()
	})

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				modalRef.current &&
				!modalRef.current.contains(event.target as Node) &&
				buttonRef.current &&
				!buttonRef.current.contains(event.target as Node)
			) {
				closeModal()
			}
		}
		if (showModal) {
			document.addEventListener('mousedown', handleClickOutside)
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [showModal])

	return (
		<div className='relative w-full lg:w-auto'>
			<div
				ref={buttonRef}
				className='flex items-center  w-full bg-white h-48 px-10 lg:w-auto'>
				<DateSVG className='w-16 h-16' />
				<input
					type='text'
					value={formatValue()}
					onClick={openModal}
					placeholder='Date'
					readOnly
					className='text-11 uppercase px-4 flex-1 bg-transparent focus:outline-none' />
				<button onClick={openModal} className='text-12'>
					<ChevronSVG className='w-9' />
				</button>
			</div>

			{showModal && (
				<div
					ref={modalRef}
					data-slideout-open={isModalOpen}
					className={`fixed inset-0 bg-white z-[999] flex flex-col pt-40 p-20 shadow-lg transition-transform duration-300 lg:absolute lg:mt-2 lg:rounded-lg lg:w-auto lg:p-12 lg:shadow-lg
						${isModalOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>

					<div className='flex items-center justify-between lg:hidden mb-12'>
						<h4 className='text-12 hidden lg:block uppercase'>Select Date</h4>
						<button onClick={closeModal} className='text-12'>
							<CloseSVG className='w-16 h-16' />
						</button>
					</div>

					<div ref={trapRef} className='flex flex-col gap-4 '>
						{showPresets && allowRange && (
							<div className='flex items-center gap-2'>
								{QUICK_DATE_OPTIONS.map(({label, name}) => (
									<Button
										variant='primary'
										small
										label={label}
										key={label}
										onClick={() => handleQuickSelection(name)}
										className='text-9' />
								))}
							</div>
						)}
						<div className='calendar-container'>
							<ReactCalendar
								onChange={onChange}
								value={value as Value}
								selectRange={allowRange}
								prev2Label={null}
								allowPartialRange
								next2Label={null}
								locale={locale}
								minDate={minDate}
								maxDate={maxDate} />
						</div>
						<div className='flex items-center justify-between'>
							<Button variant='secondary-transparent' small label='Clear' onClick={handleClear} />
							<Button variant='primary' small label='Apply' onClick={handleApply} />
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default Calendar
